import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutTemplate, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/admin/AdminUI";
import DashboardRenderer from "@/components/dashboards/DashboardRenderer";

/*
 * Painéis publicados no Design do Dashboard que o usuário pode ver (o
 * backend decide: escopo, empresa e público por perfil). A escolha fica
 * salva por usuário neste navegador; sem escolha, abre o painel marcado
 * como padrão (pessoal > empresa > plataforma) ou a visão padrão ASTRAL.
 */

const STANDARD = "standard";
const SCOPE_RANK = { personal: 0, tenant: 1, platform: 2 };

function storageKey(userId) {
  return `astral:dashboard-selected:${userId || "anon"}`;
}

function readChoice(userId) {
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function writeChoice(userId, value) {
  try {
    localStorage.setItem(storageKey(userId), value);
  } catch {
    /* navegador sem armazenamento: a escolha vale só nesta visita */
  }
}

export function usePublishedDashboards() {
  const { user } = useAuth();
  const [state, setState] = useState({ items: [], canDesign: false, loaded: false });
  const [choice, setChoice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboards")
      .then(({ data }) => !cancelled && setState({ items: data.items || [], canDesign: Boolean(data.can_design), loaded: true }))
      .catch(() => !cancelled && setState({ items: [], canDesign: false, loaded: true }));
    return () => { cancelled = true; };
  }, [user?.id]);

  const selectedId = useMemo(() => {
    if (!state.loaded) return null;
    const stored = choice ?? readChoice(user?.id);
    if (stored === STANDARD) return null;
    if (stored && state.items.some((d) => d.id === stored)) return stored;
    const defaults = state.items.filter((d) => d.is_default).sort((a, b) => SCOPE_RANK[a.scope] - SCOPE_RANK[b.scope]);
    return defaults[0]?.id || null;
  }, [state, choice, user?.id]);

  const select = useCallback((value) => {
    const next = value || STANDARD;
    writeChoice(user?.id, next);
    setChoice(next);
  }, [user?.id]);

  return { ...state, selectedId, select };
}

export function DashboardSwitcher({ items, canDesign, selectedId, select }) {
  if (!items.length && !canDesign) return null;
  return (
    <div className="flex items-center gap-2">
      {items.length > 0 && (
        <>
          <span className="text-xs uppercase text-muted-foreground">Painel</span>
          <select
            value={selectedId || STANDARD}
            onChange={(e) => select(e.target.value === STANDARD ? null : e.target.value)}
            className="h-9 max-w-[220px] rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Escolher painel"
            data-testid="dashboard-switcher"
          >
            <option value={STANDARD}>Visão padrão ASTRAL</option>
            {items.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </>
      )}
      {canDesign && (
        <Button variant="outline" size="sm" asChild data-testid="dashboard-customize">
          <Link to="/admin/dashboards"><LayoutTemplate size={14} className="mr-1" /> Personalizar</Link>
        </Button>
      )}
    </div>
  );
}

export function PublishedDashboardView({ dashboardId, switcher, canDesign }) {
  const [dashboard, setDashboard] = useState(null);
  const [library, setLibrary] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setDashboard(null);
    setError("");
    api.get(`/dashboards/${dashboardId}`)
      .then(({ data }) => setDashboard(data))
      .catch(() => setError("Este painel não está mais disponível para você."));
  }, [dashboardId]);
  useEffect(() => {
    api.get("/dashboards/widgets").then(({ data }) => setLibrary(data.widgets || [])).catch(() => setLibrary([]));
  }, []);

  return (
    <div className="space-y-5" data-testid="published-dashboard">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight">{dashboard?.name || "Dashboard"}</h1>
          {dashboard?.description && <p className="mt-1 text-sm text-muted-foreground">{dashboard.description}</p>}
          {dashboard?.published && (
            <p className="mt-1 text-[11px] text-muted-foreground">Versão {dashboard.published.version} · dados limitados ao seu escopo de acesso</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {switcher}
          {canDesign && dashboard?.can_edit && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/dashboards/${dashboardId}`}><Pencil size={14} className="mr-1" /> Editar</Link>
            </Button>
          )}
        </div>
      </div>
      {error ? (
        <p className="text-sm text-muted-foreground" role="alert">{error}</p>
      ) : !dashboard ? (
        <LoadingState />
      ) : (
        <DashboardRenderer dashboard={dashboard} library={library} />
      )}
    </div>
  );
}
